import { useIsFetching, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { FormEvent, useEffect, useState } from 'react';
import { isIOS, isMobile } from 'react-device-detect';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Color } from '@/constants/color';
import { DrugCategory } from '@/constants/programs';
import { useMembersContext } from '@/contexts/membersContext';
import {
  OnboardingContextActionType,
  OnboardingContextState,
  useOnboardingContext,
  useOnboardingDispatch
} from '@/contexts/onboardingContext';
import useTracking from '@/hooks/useTracking/useTracking';
import gtmOnboardingEventBuilder from '@/hooks/useTracking/utils/gtmOnboardingEventBuilder';
import useTranslate from '@/hooks/useTranslate';
import { InsuranceType } from '@/pages/members/constants/insurance';
import { COMPONENTS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import { MOBILE_EVENT_LOGIN, MOBILE_GA_EVENT, MOBILE_GA_EVENT_V2 } from '@/pages/members/types';
import AuthRepository from '@/repositories/members/AuthRepository';
import OnboardingRepository from '@/repositories/members/OnboardingRepository';
import { JurisdictionStatus } from '@/types/jurisdiction';
import Button from '@/ui/members/components/Button';
import Modal from '@/ui/members/components/Modal';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import { getRecaptchaToken, RecaptchaAction } from '@/utils/recaptcha';

import { OnboardingNodeType, OnboardingStepType } from '../steps/types';
import { OnboardingNode } from '../types';
import prevSubmittedStep from '../utils/firstSkippedStep';
import getFirstMatching from '../utils/getFirstMatching';
import { getInitialPayload } from '../utils/getInitialPayload';
import getSubmittedData from '../utils/getSubmittedData';
import getSubmittedNodePayload from '../utils/getSubmittedNodePayload';
import isRedirectingStep from '../utils/isRedirectingStep';
import isSkipped from '../utils/isSkipped';
import isSubmitted from '../utils/isSubmitted';
import { ONBOARDING_VERSIONS } from '../versions';

import BackButton from './BackButton';
import ProgressBar from './ProgressBar';

const TREATABLE_STATUS = 'treatable';
const WAITLISTED_STATUS = 'waitlisted';
export const ONBOARDING_QUERY_KEY_PREFIX = 'onboardingForms';

const Stepper = () => {
  const onboardingContext = useOnboardingContext();
  const { addMessage, isRunningInReactNativeWebView } = useMembersContext();
  const dispatch = useOnboardingDispatch();
  const navigate = useNavigate();
  const onboardingRepository = OnboardingRepository();
  const authRepository = AuthRepository();
  const { mixpanel, gtm } = useTracking();
  const { t } = useTranslate(`${COMPONENTS_PATH_TRANSLATION_KEY}.Stepper`);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const enableAppTrackingTransparency = window.ENABLE_APP_TRACKING_TRANSPARENCY;
  const [queryParams, _] = useSearchParams();

  const { step: urlStep } = useParams<{ version: string; step: string }>();

  const steps = ONBOARDING_VERSIONS[onboardingContext.version];
  const currentNode = steps.find(
    (step: OnboardingNode) => step.index === onboardingContext.step - 1
  )!;
  const pendingFormQueries = useIsFetching({
    queryKey: [ONBOARDING_QUERY_KEY_PREFIX]
  });

  useEffect(() => {
    const handlePopState = () => {
      if (mutation.isPending) {
        return;
      }
      if (!onboardingContext.totalSteps) {
        return;
      }
      if (window.location.pathname.includes('/onboarding/success')) {
        return;
      }
      if (Number(urlStep) < onboardingContext.step) {
        const navigateTo = prevSubmittedStep(
          onboardingContext.step,
          onboardingContext.stepsSequence,
          onboardingContext.steps
        );
        dispatch({
          type: OnboardingContextActionType.PREV_STEP
        });
        navigate(`/onboarding/${onboardingContext.version}/${navigateTo}`, {
          replace: true
        });
        return;
      }
      if (Number(urlStep) > onboardingContext.step) {
        dispatch({
          type: OnboardingContextActionType.NEXT_STEP
        });
        navigate(`/onboarding/${onboardingContext.version}/${onboardingContext.step + 1}`, {
          replace: true
        });
        return;
      }
    };

    handlePopState();
  }, [urlStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentElement = getFirstMatching(
    currentNode.elements,
    onboardingContext.steps,
    onboardingContext.formOptions
  )!;
  const currentStepProps = currentElement.component.props;
  const isLastStep = onboardingContext.step === onboardingContext.totalSteps;
  const mutateCurrentStep =
    isSubmitted(currentNode.key, currentStepProps.uid, onboardingContext.steps) ||
    isSkipped(currentNode.key, currentStepProps.uid, onboardingContext.steps);

  const getOnboardingEvents = (treatableOrWaitlisted: boolean, pltv?: number | null) => {
    const insuranceType = (
      getSubmittedNodePayload(OnboardingNodeType.INSURANCE_TYPE, onboardingContext.steps) as {
        insuranceType?: string;
      }
    )?.insuranceType as InsuranceType;

    const jurisdictionStatus = (
      getSubmittedNodePayload(OnboardingNodeType.JURISDICTION, onboardingContext.steps) as {
        jurisdictionStatus?: string;
      }
    )?.jurisdictionStatus as JurisdictionStatus;

    const whatDoYouNeedHelpWith = getSubmittedNodePayload(
      OnboardingNodeType.PRIMARY_SUBSTANCE,
      onboardingContext.steps
    ) as {
      drugCategory: DrugCategory;
      primarySubstance: string;
    };

    return gtmOnboardingEventBuilder(
      insuranceType,
      treatableOrWaitlisted,
      jurisdictionStatus === JurisdictionStatus.Active,
      whatDoYouNeedHelpWith?.drugCategory,
      whatDoYouNeedHelpWith?.primarySubstance,
      pltv
    );
  };

  // The backend only checks the steps that create the account records.
  const recaptchaAction: RecaptchaAction | null = isLastStep
    ? 'signup'
    : currentNode.key === OnboardingNodeType.EMAIL
      ? 'signup_start'
      : null;

  const mutation = useMutation({
    mutationFn: async (data: OnboardingContextState): Promise<OnboardingContextState> => {
      const recaptchaToken = recaptchaAction
        ? await getRecaptchaToken(onboardingContext.recaptchaSiteKey, recaptchaAction)
        : null;
      return await onboardingRepository.post(data, recaptchaToken);
    },
    onError: (error) => {
      mixpanel.trackError(
        error.message,
        `onboarding:${currentStepProps.nodeType}:${currentStepProps.uid}`,
        {
          version: onboardingContext.version,
          feature: 'unified-onboarding'
        }
      );
      dispatch({
        type: OnboardingContextActionType.UNDO_SUBMIT,
        payload: {
          nodeType: currentNode.key,
          stepName: currentStepProps.uid
        }
      });

      dispatch({
        type: OnboardingContextActionType.SET_STEP_ERROR,
        payload: mapAxiosErrorMessages(error as AxiosError)[0].text
      });

      if (!currentNode.options?.hideErrorToast) {
        addMessage(mapAxiosErrorMessages(error as AxiosError));
      }
    },
    onSuccess: (response: OnboardingContextState) => {
      dispatch({
        type: OnboardingContextActionType.CLEAR_STEP_ERROR
      });
      if (!onboardingContext.personId && response.personId) {
        dispatch({
          type: OnboardingContextActionType.SET_PERSON_ID,
          payload: response.personId
        });
        mixpanel.identify(response.personId);
        mixpanel.registerSuperProperties({
          personId: response.personId
        });
      }

      if (response.sessionId) {
        mixpanel.registerSuperProperties({
          sessionId: response.sessionId
        });
      }

      if (response._experiment?.enabled && response._experiment.eligible !== false) {
        mixpanel.registerSuperProperties({
          experiments: [response._experiment]
        });
      }

      const primarySubstanceData = getSubmittedData<{
        drugCategory: string;
        primarySubstance: string;
      }>(
        OnboardingNodeType.PRIMARY_SUBSTANCE,
        OnboardingStepType.PRIMARY_SUBSTANCE,
        onboardingContext.steps
      );

      if (primarySubstanceData?.drugCategory && primarySubstanceData?.primarySubstance) {
        mixpanel.registerSuperProperties({
          concerns: [
            {
              parents: [primarySubstanceData.drugCategory],
              name: primarySubstanceData.primarySubstance
            }
          ]
        });
      }

      if (isLastStep) {
        setIsFinishing(true);
        mixpanel.track('onboarding:completed', {
          version: onboardingContext.version,
          feature: 'unified-onboarding'
        });
        const allEvents = getOnboardingEvents(
          response.treatabilityStatus === TREATABLE_STATUS ||
            response.treatabilityStatus === WAITLISTED_STATUS,
          response.pltvData?.pltv
        );
        if (!isRunningInReactNativeWebView) {
          allEvents.forEach(({ name, args }) => {
            gtm.track(name, true, args, {
              version: onboardingContext.version,
              feature: 'unified-onboarding'
            }); // also send to mixpanel
          });
        } else {
          window.ReactNativeWebView!.postMessage(
            JSON.stringify({
              eventType: MOBILE_GA_EVENT,
              payload: allEvents.filter((e) => e.name !== 'onboarding_value').map((e) => e.name)
            })
          );
          window.ReactNativeWebView!.postMessage(
            JSON.stringify({
              eventType: MOBILE_GA_EVENT_V2,
              payload: allEvents
            })
          );
        }

        if (isRunningInReactNativeWebView) {
          handleFinishOnMobile();
          return;
        }
        handleFinishOnWeb();
        return;
      }

      if (response.step === 1 && !response.initialized) {
        dispatch({
          type: OnboardingContextActionType.INIT,
          payload: getInitialPayload(response, queryParams)
        });
        navigate(`/onboarding/${response.version}/1`, { replace: true });
        return;
      }

      dispatch({
        type: OnboardingContextActionType.NEXT_STEP
      });
      navigate(`/onboarding/${onboardingContext.version}/${onboardingContext.step + 1}`);
    }
  });

  const requestTrackingFlow = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      const handleATTResult = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          if (data.eventType === 'TRACKING_PERMISSION_RESULT') {
            window.removeEventListener('message', handleATTResult);
            resolve();
          }
        } catch {
          // a lot of different things (extensions, debuggers) post messages
          // logging all errors here flood the console.
          return;
        }
      };

      window.addEventListener('message', handleATTResult);

      setShowTrackingModal(true);
      mixpanel.track('onboarding:att_prompt_shown', {
        version: onboardingContext.version,
        feature: 'unified-onboarding'
      });
    });
  };

  const handleFinishOnMobile = async () => {
    // We are using the false condition below to act as
    // feature disabling condition.
    // When we want to enable it we will remove the '&& false'

    if (isIOS && enableAppTrackingTransparency) {
      await requestTrackingFlow();
    }

    // continue login flow after ATT finished or if non-iOS
    const response = await authRepository.getOneTimeToken();
    window.ReactNativeWebView!.postMessage(
      JSON.stringify({ eventType: MOBILE_EVENT_LOGIN, payload: response })
    );
  };

  const handleAcceptTracking = () => {
    setShowTrackingModal(false);
    mixpanel.track('onboarding:att_prompt_accepted', {
      version: onboardingContext.version,
      feature: 'unified-onboarding'
    });
    window.ReactNativeWebView!.postMessage(
      JSON.stringify({ eventType: 'REQUEST_TRACKING_PERMISSION' })
    );
  };

  const handleDeclineTracking = async () => {
    setShowTrackingModal(false);
    mixpanel.track('onboarding:att_prompt_declined', {
      version: onboardingContext.version,
      feature: 'unified-onboarding'
    });
    // Skip ATT, continue directly
    const response = await authRepository.getOneTimeToken();
    window.ReactNativeWebView!.postMessage(
      JSON.stringify({ eventType: MOBILE_EVENT_LOGIN, payload: response })
    );
  };

  // Listen for ReactNative telling us ATT finished
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      // Other senders share this channel, e.g. reCAPTCHA posts "recaptcha-setup"
      if (typeof event.data !== 'string' || !event.data.startsWith('{')) {
        return;
      }
      try {
        const data = JSON.parse(event.data);

        if (data.eventType === 'ATT_FINISHED') {
          // Now safe to continue login
          authRepository.getOneTimeToken().then((response) => {
            window.ReactNativeWebView!.postMessage(
              JSON.stringify({
                eventType: MOBILE_EVENT_LOGIN,
                payload: response
              })
            );
          });
        }
      } catch (err) {
        console.error('Bad message from RN:', err);
      }
    };

    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFinishOnWeb = () => {
    // send and navigate, don't wait for it to finish
    authRepository.sendOneTimeToken();
    navigate(`/onboarding/success`, { replace: true });
    return;
  };

  const getButtonText = () => {
    if (!isRedirectingStep(currentStepProps)) {
      return currentStepProps.actionButtonText;
    }

    return isLastStep ? t('finish') : t('continue');
  };

  const handleNextStep = (event: FormEvent) => {
    event.preventDefault();
    mixpanel.trackButtonPress(
      {
        buttonTitle: 'Next',
        buttonText: getButtonText(),
        screenName: `onboarding:${currentStepProps.nodeType}:${currentStepProps.uid}`
      },
      {
        version: onboardingContext.version,
        feature: 'unified-onboarding'
      }
    );
    mixpanel.trackOnboardingResolvedStep(
      currentStepProps.nodeType,
      onboardingContext.version,
      'completed'
    );
    !isRedirectingStep(currentStepProps) &&
      dispatch({
        type: OnboardingContextActionType.TRIGGER_SUBMIT,
        payload: {
          node: currentNode.key,
          uid: currentStepProps.uid
        }
      });
  };

  const handleSkipStep = () => {
    mixpanel.trackButtonPress(
      {
        buttonTitle: 'Skip',
        buttonText: 'Skip',
        screenName: `onboarding:${currentStepProps.nodeType}:${currentStepProps.uid}`
      },
      {
        version: onboardingContext.version,
        feature: 'unified-onboarding'
      }
    );
    mixpanel.trackOnboardingResolvedStep(
      currentStepProps.nodeType,
      onboardingContext.version,
      'skipped'
    );
    !isRedirectingStep(currentStepProps) &&
      dispatch({
        type: OnboardingContextActionType.SKIP,
        payload: {
          uid: currentStepProps.uid,
          node: currentNode.key
        }
      });
  };

  useEffect(() => {
    if (
      onboardingContext.completed &&
      onboardingContext.loginLink &&
      !isRunningInReactNativeWebView
    ) {
      navigate(`/onboarding/success`);
      return;
    }
  });

  useEffect(() => {
    if (
      !isRedirectingStep(currentStepProps) &&
      (isSubmitted(currentNode.key, currentStepProps.uid, onboardingContext.steps) ||
        isSkipped(currentNode.key, currentStepProps.uid, onboardingContext.steps))
    ) {
      if (!onboardingContext.loginLink) {
        mutation.mutate(onboardingContext);
      }
    }
  }, [mutateCurrentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const bottomBarStyles = isMobile
    ? 'fixed bottom-0 left-0 right-0 z-10 p-4 dark:bg-uniform-gray-900 light:bg-uniform-white-base'
    : 'mt-2 flex-end z-10';
  const componentStyles = isMobile && !currentElement.additionalContent ? 'pb-18' : 'pb-4';

  return (
    <>
      <form className="flex flex-col flex-1 h-full">
        <div className="mb-4">
          <BackButton disabled={mutation.isPending} />
        </div>

        <div className="flex flex-col items-center">
          <ProgressBar />
        </div>

        <div className={componentStyles}>
          <div>{currentElement.component}</div>

          <div className={bottomBarStyles}>
            <div className="flex flex-row gap-2">
              {currentNode.options?.optional && (
                <Button
                  color={Color.GRAY}
                  onClick={handleSkipStep}
                  className="py-4 w-1/3"
                  disabled={
                    isFinishing || mutation.isPending || pendingFormQueries > 0 || mutateCurrentStep
                  }
                  loading={
                    mutation.isPending &&
                    isSkipped(currentNode.key, currentStepProps.uid, onboardingContext.steps)
                  }
                  spinnerVisibleLabel={false}>
                  {t('skip')}
                </Button>
              )}
              <Button
                onClick={handleNextStep}
                className={`w-full dark:bg-uniform-green-800 py-4 ${
                  !onboardingContext.isCurrentStepValid ? 'opacity-50' : ''
                }`}
                color={Color.GREEN}
                loading={
                  isFinishing ||
                  (mutation.isPending &&
                    isSubmitted(currentNode.key, currentStepProps.uid, onboardingContext.steps))
                }
                disabled={
                  isFinishing || mutation.isPending || pendingFormQueries > 0 || mutateCurrentStep
                }
                type="submit">
                {getButtonText()}
              </Button>
            </div>
            {recaptchaAction && onboardingContext.recaptchaSiteKey && (
              <p className="mt-2 text-center text-xs">{t('recaptchaDisclosure')}</p>
            )}
          </div>
        </div>

        {/* Add additional content beneath CTA */}
        <div className="mb-auto z-1 pb-18">{currentElement.additionalContent}</div>
      </form>

      <Modal
        buttonContent="Allow Ad Tracking"
        dismissButton={{
          buttonAriaLabel: "Don't Allow",
          buttonContent: "Don't Allow",
          mainButtonAction: () => handleAcceptTracking()
        }}
        leadingImage="/images/onboarding/allow-ad-tracking.png"
        onClose={handleDeclineTracking}
        open={showTrackingModal}
        textContent="Allowing ad tracking provides anonymous data to improve your experience and to help others find the care they need. This data is only shared anonymously. At Affect, we always prioritize your privacy and security."
        title="Help us grow"
      />
    </>
  );
};
export default Stepper;
